import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Clock, ArrowLeft, Loader2, MapPin } from 'lucide-react';

import { useLot } from '../contexts/LotContext';
import { supabase } from '../lib/supabase';
import { addHours, format, isAfter } from 'date-fns';
import toast from 'react-hot-toast';
import Footer from './Footer';

interface ParkingSession {
  id: string;
  license_plate: string;
  phone: string;
  hours: number;
  expires_at: string;
  location?: string;
  email?: string;
}

function ExtendTime() {
  const { sessionId } = useParams<{ sessionId:string }>();
  const navigate = useNavigate();
  const { lot, settings, isLoading, error } = useLot();
  const [session, setSession] = useState<ParkingSession | null>(null);
  const [extendHours, setExtendHours] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      toast.error('Invalid session ID');
      navigate(`/lots/${lot?.slug || 'adeline-street'}`);
      return;
    }

    try {
      // First get the current session to get license plate and phone
      const { data: currentSession, error: currentError } = await supabase
        .from('parking_sessions')
        .select('*, lots!inner(slug)')
        .eq('id', sessionId)
        .single();

      if (currentError) throw currentError;
      if (!currentSession) throw new Error('Session not found');

      // If the lot from the session doesn't match the URL, redirect
      if (currentSession.lots?.slug !== lot?.slug) {
          console.log(`[ExtendTime] Mismatch between session lot (${currentSession.lots?.slug}) and URL lot (${lot?.slug}). Redirecting...`);
          navigate(`/lots/${currentSession.lots?.slug}/extend/${sessionId}`, { replace: true });
          return;
      }


      // If no paid session exists, use the current session
      setSession(currentSession);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load session';
      toast.error(errorMessage);
      navigate(`/lots/${lot?.slug || 'adeline-street'}`);
    } finally {
      setLoading(false);
    }
  }, [sessionId, lot, navigate]);

  useEffect(() => {
    // Wait for lot data to be loaded before fetching session
    if (lot) {
      fetchSession();
    }
  }, [lot, fetchSession]);

  const calculateExtendedAmount = () => {
    if (!settings) return 0;
    if (extendHours <= 3) {
      return extendHours * settings.hourly_rate;
    }
    return settings.max_daily_rate;
  };

  const handleExtendTime = async () => {
    if (isSubmitting) return;
    console.log('🔧 EXTENSION DEBUG: Starting extension process');
    console.log('🔧 Session data:', session);
    console.log('🔧 Lot data:', lot);
    console.log('🔧 Extend hours:', extendHours);
    
    if (!session) {
      console.error('🔧 No session data available');
      return;
    }
    
    // Check for invalid hours
    if (extendHours <= 0) {
      console.error('🔧 Invalid extend hours:', extendHours);
      toast.error('Please enter a valid number of hours');
      return;
    }

    setIsSubmitting(true);
    try {
      // Calculate new expiry time
      const currentExpiry = new Date(session.expires_at);
      const now = new Date();
      const startTime = isAfter(now, currentExpiry) ? now : currentExpiry;
      const newExpiryTime = addHours(startTime, extendHours > 3 ? 12 : extendHours);
      const amount = calculateExtendedAmount();

      // Validate required lot information
      if (!lot?.id) {
        console.error('🔧 Missing lot ID:', lot);
        throw new Error('Lot information is required for extensions');
      }

      console.log('🔧 Creating extension session with data:', {
        license_plate: session.license_plate,
        phone: session.phone,
        hours: extendHours,
        amount: amount,
        lot_id: lot.id,
        lot_slug: lot.slug,
        location: lot.display_name || lot.name
      });

      // Create a new parking session for the extension
      const { data: newSession, error } = await supabase
        .from('parking_sessions')
        .insert({
          license_plate: session.license_plate,
          phone: session.phone,
          hours: extendHours,
          amount: amount,
          expires_at: newExpiryTime.toISOString(),
          location: lot.display_name || lot.name, // Use current lot's display name
          lot_id: lot.id, // REQUIRED: Associate with the current lot
          email: session.email || null, // Copy email if it exists
          auto_renew: false, // Explicitly set to false for extensions
          session_type: 'Extension' // Flag this as an extension
        })
        .select()
        .single();

      if (error) {
        console.error('🔧 Database error creating session:', error);
        throw error;
      }
      if (!newSession) {
        console.error('🔧 No session returned from database');
        throw new Error('Failed to create extension session');
      }

      console.log('🔧 Extension session created successfully:', newSession.id);
      console.log('🔧 Navigating to:', `/lots/${lot.slug}/payment/${newSession.id}`);

      // Redirect to payment page for the new session (FIXED: Aberdeen Street slug)
      navigate(`/lots/${lot.slug}/payment/${newSession.id}`);
    } catch (err) {
      console.error('🔧 EXTENSION ERROR:', err);
      console.error('🔧 Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        session: session,
        lot: lot,
        extendHours: extendHours
      });
      const errorMessage = err instanceof Error ? err.message : 'Failed to extend parking time';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!settings) return;
    const value = parseInt(e.target.value, 10);
    if (value > settings.max_hours) {
      toast.error(`You can only park for a maximum of ${settings.max_hours} hours.`);
      setExtendHours(settings.max_hours);
    } else {
      setExtendHours(value);
    }
  };

  // Show loading state if lot data or session data is still loading
  if (isLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show error state if lot data failed to load or session not found
  if (error || !session || !lot || !settings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 text-center">
          <p className="text-red-600 mb-4">{error || 'Session not found or lot data could not be loaded.'}</p>
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const isExpired = new Date(session.expires_at) < new Date();
  const startTime = isAfter(new Date(), new Date(session.expires_at))
    ? new Date()
    : new Date(session.expires_at);
  const newExpiryTime = addHours(startTime, extendHours > 3 ? 12 : extendHours);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex shadow-md rounded overflow-hidden h-6">
              <div className="w-4 h-full bg-green-600"></div>
              <div className="w-4 h-full bg-white"></div>
              <div className="w-4 h-full bg-red-600"></div>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Parking Little Italy</h1>
          </div>
        
        <div className="bg-white p-6 rounded-xl shadow-lg space-y-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${isExpired ? 'bg-red-100' : 'bg-green-100'}`}>
              <Clock className={`h-6 w-6 ${isExpired ? 'text-red-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="font-semibold text-gray-800">
                {session.license_plate}
              </p>
              <p className={`text-sm ${isExpired ? 'text-red-600' : 'text-gray-600'}`}>
                {isExpired ? 'Expired' : 'Expires'}: {format(new Date(session.expires_at), 'PPp')}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Additional Hours</label>
              <div className="relative">
                <input
                  type="number"
                  required
                  min="1"
                  max={settings.max_hours}
                  value={extendHours || ''}
                  onChange={handleHoursChange}
                  className="w-20 rounded-lg border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                />
              </div>
            </div>
            <p className="text-sm text-gray-600">
              {extendHours <= 3
                ? `$${settings.hourly_rate.toFixed(2)}/hour for up to 3 hours`
                : `Maximum daily rate of $${settings.max_daily_rate.toFixed(2)} applied`}
            </p>
            <p className="text-lg font-semibold text-blue-600 mt-2">
              Total: ${calculateExtendedAmount().toFixed(2)}
            </p>
            <p className="text-sm text-gray-600">
              New expiry: {format(newExpiryTime, 'PPp')}
            </p>
          </div>

          <button
            onClick={handleExtendTime}
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Processing...
              </>
            ) : (
              'Continue to Payment'
            )}
          </button>

          <Link
            to={`/lots/${lot?.slug || 'adeline-street'}/payment/complete/${session.id}`}
            className="block w-full bg-gray-100 text-gray-700 text-center py-2 px-4 rounded-lg hover:bg-gray-200 transition duration-200"
          >
            Cancel
          </Link>
        </div>

        <Footer />
      </div>
    </div>
  );
}

export default ExtendTime;